import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import PlatformIcon from '../components/PlatformIcon';
import './Dashboard.css';

const PERIODS = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: 'all', label: 'All time' },
];

const formatNumber = (value) => (Number(value) || 0).toLocaleString();
const formatPercent = (value) => `${Number(value || 0).toFixed(1).replace('.0', '')}%`;

const platformLabel = (platform) => {
  if (platform === 'ios') return 'iOS';
  if (platform === 'android') return 'Android';
  return platform === 'unknown' ? 'Unknown' : platform;
};

const StatCard = ({ label, value, detail, tone = 'blue' }) => (
  <div className={`device-stat-card device-stat-${tone}`}>
    <span>{label}</span>
    <strong>{value}</strong>
    <small>{detail}</small>
  </div>
);

const PlatformLabel = ({ platform }) => (
  <span className="device-platform-label">
    <PlatformIcon platform={platform} size={16} />
    {platformLabel(platform)}
  </span>
);

const DistributionList = ({ rows, labelKey, emptyText }) => {
  if (!rows.length) return <div className="device-empty-small">{emptyText}</div>;
  const max = Math.max(...rows.map((row) => Number(row.device_count) || 0), 1);

  return (
    <div className="device-distribution-list">
      {rows.map((row, index) => (
        <div className="device-distribution-row" key={`${row.platform || ''}-${row[labelKey]}-${index}`}>
          <div className="device-distribution-heading">
            <span className="device-distribution-name">
              {row.platform && <PlatformIcon platform={row.platform} size={14} />}
              {row[labelKey]}
            </span>
            <span>
              <strong>{formatNumber(row.device_count)}</strong>
              <small>{formatPercent(row.percentage)}</small>
            </span>
          </div>
          <div className="device-progress" aria-hidden="true">
            <span style={{ width: `${(Number(row.device_count) / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const Dashboard = () => {
  const [generalStats, setGeneralStats] = useState(null);
  const [deviceStats, setDeviceStats] = useState(null);
  const [period, setPeriod] = useState('30');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const { fetchWithAuth } = useAuth();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchWithAuth('/api/stats');
        if (!response.ok) throw new Error('Failed to load general statistics');
        if (!cancelled) setGeneralStats(await response.json());
      } catch (_) {
        // Device analytics remains useful even when one of the legacy counters
        // is temporarily unavailable.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    (async () => {
      deviceStats ? setRefreshing(true) : setLoading(true);
      try {
        const response = await fetchWithAuth(`/api/analytics/devices?period=${period}`, {
          signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to load device analytics');
        if (!cancelled) {
          setDeviceStats(data);
          setError(null);
        }
      } catch (loadError) {
        if (!cancelled && loadError.name !== 'AbortError') setError(loadError.message);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    })();
    return () => { cancelled = true; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const topOsVersions = useMemo(
    () => (deviceStats?.osVersions || []).slice(0, 10),
    [deviceStats]
  );
  const topModels = useMemo(
    () => (deviceStats?.deviceModels || []).slice(0, 10),
    [deviceStats]
  );

  if (loading && !deviceStats) return <div className="loading">Loading dashboard...</div>;

  const summary = deviceStats?.summary || {};
  const currentRelease = deviceStats?.currentRelease;

  return (
    <div className="device-dashboard">
      <div className="device-page-heading">
        <div>
          <h1>App & Device Analytics</h1>
          <p>Installations, operating systems and release adoption.</p>
        </div>
        <div className="device-period-control" aria-label="Analytics period">
          {PERIODS.map((item) => (
            <button
              type="button"
              key={item.value}
              className={period === item.value ? 'is-active' : ''}
              onClick={() => setPeriod(item.value)}
              disabled={refreshing}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error">Error: {error}</div>}

      {deviceStats && !deviceStats.available && (
        <div className="device-setup-state">
          <div className="device-setup-icon">⌁</div>
          <div>
            <strong>Waiting for device data</strong>
            <p>
              The <code>user_devices</code> migration has not been applied in this environment yet.
              This dashboard will populate automatically after the mobile API starts receiving device syncs.
            </p>
          </div>
        </div>
      )}

      <div className={`device-dashboard-content ${refreshing ? 'is-refreshing' : ''}`}>
        <div className="device-stat-grid">
          <StatCard
            label="Devices"
            value={formatNumber(summary.totalDevices)}
            detail="Unique app installations"
          />
          <StatCard
            label="Users"
            value={formatNumber(summary.uniqueUsers)}
            detail={`${formatPercent(summary.trackedUsersPercent)} of all users tracked`}
            tone="violet"
          />
          <StatCard
            label="Active in 24h"
            value={formatNumber(summary.active24h)}
            detail="Devices seen in foreground"
            tone="green"
          />
          <StatCard
            label="Latest release"
            value={currentRelease ? `v${currentRelease.appVersion}` : '—'}
            detail={currentRelease
              ? `Build ${currentRelease.buildNumber} · ${formatPercent(currentRelease.adoptionPercent)} adoption`
              : 'No release data yet'}
            tone="orange"
          />
        </div>

        <div className="device-overview-grid">
          <section className="device-panel">
            <div className="device-panel-heading">
              <div>
                <h2>Operating systems</h2>
                <p>Device distribution by platform</p>
              </div>
            </div>
            <div className="device-platform-grid">
              {(deviceStats?.platforms || []).map((row) => (
                <div className={`device-platform-card platform-${row.platform}`} key={row.platform}>
                  <div className="device-platform-icon-wrap">
                    <PlatformIcon platform={row.platform} size={24} />
                    {!['ios', 'android'].includes(row.platform) && '·'}
                  </div>
                  <div>
                    <PlatformLabel platform={row.platform} />
                    <strong>{formatNumber(row.device_count)}</strong>
                    <span>{formatPercent(row.percentage)} of devices</span>
                  </div>
                </div>
              ))}
              {(deviceStats?.platforms || []).length === 0 && (
                <div className="device-empty-small">No platform data yet.</div>
              )}
            </div>
          </section>

          <section className="device-panel device-release-panel">
            <div className="device-panel-heading">
              <div>
                <h2>Latest build</h2>
                <p>Most recently seen app release</p>
              </div>
              <span className="device-live-badge">LIVE</span>
            </div>
            {currentRelease ? (
              <div className="device-current-release">
                <div className="device-release-version">
                  <span>App version</span>
                  <strong>{currentRelease.appVersion}</strong>
                </div>
                <div className="device-build-number">
                  <span>Build number</span>
                  <strong>{currentRelease.buildNumber}</strong>
                </div>
                <div className="device-release-adoption">
                  <div>
                    <span>Release adoption</span>
                    <strong>{formatPercent(currentRelease.adoptionPercent)}</strong>
                  </div>
                  <div className="device-progress device-progress-green">
                    <span style={{ width: `${Math.min(100, currentRelease.adoptionPercent)}%` }} />
                  </div>
                  <small>{formatNumber(currentRelease.deviceCount)} devices · {formatNumber(currentRelease.userCount)} users</small>
                </div>
              </div>
            ) : (
              <div className="device-empty-small">No app version and build data yet.</div>
            )}
          </section>
        </div>

        <div className="device-overview-grid">
          <section className="device-panel">
            <div className="device-panel-heading">
              <div>
                <h2>OS versions</h2>
                <p>Top versions in the selected period</p>
              </div>
            </div>
            <DistributionList rows={topOsVersions} labelKey="os_version" emptyText="No OS version data yet." />
          </section>

          <section className="device-panel">
            <div className="device-panel-heading">
              <div>
                <h2>Device models</h2>
                <p>Most used hardware models</p>
              </div>
            </div>
            <DistributionList rows={topModels} labelKey="device_model" emptyText="No device model data yet." />
          </section>
        </div>

        <section className="device-panel">
          <div className="device-panel-heading">
            <div>
              <h2>App versions & builds</h2>
              <p>Release adoption across active installations</p>
            </div>
            <span className="device-row-count">{formatNumber(deviceStats?.releases?.length)} releases</span>
          </div>
          <div className="table-container">
            <table className="device-release-table">
              <thead>
                <tr>
                  <th>App version</th>
                  <th>Build number</th>
                  <th>Devices</th>
                  <th>Users</th>
                  <th>Share</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {(deviceStats?.releases || []).map((release, index) => (
                  <tr key={`${release.app_version}-${release.build_number}-${index}`}>
                    <td><strong>{release.app_version}</strong></td>
                    <td><code>{release.build_number}</code></td>
                    <td>{formatNumber(release.device_count)}</td>
                    <td>{formatNumber(release.user_count)}</td>
                    <td>
                      <div className="device-table-share">
                        <div className="device-progress"><span style={{ width: `${Math.min(100, release.percentage)}%` }} /></div>
                        <span>{formatPercent(release.percentage)}</span>
                      </div>
                    </td>
                    <td>{release.last_seen_at ? new Date(release.last_seen_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(deviceStats?.releases || []).length === 0 && (
              <div className="device-empty-table">No app release data for this period.</div>
            )}
          </div>
        </section>

        {generalStats && (
          <section className="device-general-strip" aria-label="General application statistics">
            <div><span>Total users</span><strong>{formatNumber(generalStats.users)}</strong></div>
            <div><span>Food logs</span><strong>{formatNumber(generalStats.foods)}</strong></div>
            <div><span>Weight records</span><strong>{formatNumber(generalStats.weights)}</strong></div>
            <div><span>Activities</span><strong>{formatNumber(generalStats.activities)}</strong></div>
          </section>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
